/**
 * Minimal, dependency-free JWT helpers. We only ever read the `exp` claim to
 * decide when an operator (iv3api) token is close to expiry and should be
 * refreshed — we never trust these values for authorization (that stays on the
 * server). Decoding is best-effort: any malformed token yields `null`.
 */

interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

function base64UrlDecode(segment: string): string | null {
  try {
    const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
    const withPad = padded + "=".repeat((4 - (padded.length % 4)) % 4);
    return atob(withPad);
  } catch {
    return null;
  }
}

export function decodeJwt(token: string): JwtPayload | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  const json = base64UrlDecode(parts[1]);
  if (!json) return null;
  try {
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** Returns the token's expiry as epoch milliseconds, or `null` if unknown. */
export function getJwtExpiryMs(token: string): number | null {
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== "number") return null;
  return payload.exp * 1000;
}

/**
 * True when the token is missing, unreadable, or within `skewMs` of expiry.
 * A token with no readable `exp` is treated as expired so we err toward
 * re-verifying rather than sending a dead token.
 */
export function isJwtExpired(token: string, skewMs = 60_000): boolean {
  if (!token.trim()) return true;
  const expMs = getJwtExpiryMs(token);
  if (expMs == null) return true;
  return Date.now() >= expMs - skewMs;
}
